import { type File } from '@littlebird/sdk';

import { type HydratedFile, useHydratedFiles } from '@/hooks/useHydratedFile';
import { isCanvasFile } from '@/utils/files';

export type ReportBody =
  | {
      layout: 'text';
      isCanvas: false;
      artifact?: undefined;
      artifactId?: undefined;
    }
  | {
      layout: 'pending' | 'artifact' | 'artifact-and-text';
      isCanvas: boolean;
      artifact: HydratedFile;
      artifactId: string;
    };

export type ReportArtifactChoice = {
  file: File | undefined;
  index: number;
  isCanvas: boolean;
};

export const chooseReportArtifact = (
  files: readonly (File | undefined)[]
): ReportArtifactChoice | undefined => {
  if (files.length === 0) return undefined;

  const canvasIndex = files.findIndex(
    (file) => file && isCanvasFile(file.contentType, file.filename)
  );
  const index = canvasIndex === -1 ? 0 : canvasIndex;
  return { file: files[index], index, isCanvas: canvasIndex !== -1 };
};

export const useReportBody = (
  artifactFileIds: string[] | undefined
): ReportBody => {
  const ids = artifactFileIds ?? [];
  const artifacts = useHydratedFiles(ids);

  if (artifacts.length === 0) return { layout: 'text', isCanvas: false };

  const choice = chooseReportArtifact(
    artifacts.map((artifact) => artifact.file)
  );
  if (!choice) return { layout: 'text', isCanvas: false };

  const { index, isCanvas: choseCanvas } = choice;
  const artifact = artifacts[index];
  const chosen = { artifact, artifactId: ids[index] } as const;

  if (artifacts.some((candidate) => candidate.status === 'loading')) {
    return { layout: 'pending', isCanvas: choseCanvas, ...chosen };
  }
  if (artifact.status === 'unavailable' && artifacts.length === 1) {
    return { layout: 'text', isCanvas: false };
  }
  const standsAlone = choseCanvas || artifacts.length === 1;
  return {
    layout: standsAlone ? 'artifact' : 'artifact-and-text',
    isCanvas: choseCanvas,
    ...chosen,
  };
};
